import { useState, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import { EntityRefLink, entityRouteRef } from '@backstage/plugin-catalog-react';
import { BreadcrumbEntry, useRouteRef } from '@backstage/frontend-plugin-api';
import {
  Alert,
  Container,
  Flex,
  Header,
  Text,
  Card,
  CardHeader,
  CardBody,
  Grid,
} from '@backstage/ui';
import { normalizeOwnerEntityRef } from '@internal/backstage-plugin-pulse-common';
import {
  useCheckDetails,
  useGroupFilter,
  useTeamOwnerFilter,
  usePulseFactCollectors,
} from '../../hooks';
import { getDescendantOwnerRefs } from '../insights/insights-group-scope';
import { FilterBar } from '../filter-bar';
import { PulseDetailsSkeleton } from '../details-skeleton';
import { EmptyState } from '../empty-state';
import {
  describeCheckCadence,
  describeCheckSavedNotice,
} from './check-schedule';
import {
  EMPTY_FILTERS,
  buildEntityOwnerSections,
  hasActiveEntitySelection,
  type Filters,
} from '../filters';
import {
  createColumns,
  ResultsTable,
  type ResultsTableRow,
  type StatusFilterOption,
  getKindOptions,
  getLifecycleOptions,
  filterRowsByGroups,
  filterRowsByKinds,
  filterRowsByLifecycles,
} from './checks-results-table';
import { CheckHistoryChartCard } from './check-history-chart-card';
import { CheckActionsButton } from './check-actions';
import { checksDetailsSubRouteRef, checksRouteRef } from '../../routes';
import { formatEntityFilter } from '../integrations/formatters';

const formatTimestamp = (timestamp?: string): string => {
  if (!timestamp) {
    return 'Not available';
  }

  const parsed = DateTime.fromISO(timestamp).setZone('Europe/Stockholm');
  return parsed.isValid ? parsed.toFormat('yyyy-MM-dd HH:mm') : '-';
};

export const PulseChecksDetails = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const getChecksPath = useRouteRef(checksRouteRef);
  const getDetailsPath = useRouteRef(checksDetailsSubRouteRef);
  const { value: checkDetails, check, loading, error } = useCheckDetails(id);
  const { value: integrations } = usePulseFactCollectors();
  // Success banner state seeded from the create/edit navigation.
  const [savedNotice, setSavedNotice] = useState<'created' | 'updated' | null>(
    (location.state as { pulseCheckSaved?: 'created' | 'updated' } | null)
      ?.pulseCheckSaved ?? null,
  );
  const [statusFilter, setStatusFilter] =
    useState<StatusFilterOption>('affected');
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const { selectedGroups, setSelectedGroups } = useGroupFilter();
  const { teamOptions, groupOptions, ownerByRef } = useTeamOwnerFilter();
  const entityRoute = useRouteRef(entityRouteRef);
  const columns = createColumns(entityRoute, id);
  const breadcrumbLabel = checkDetails?.check.name ?? check?.name ?? id;
  const checksPath = getChecksPath?.() ?? '..';
  const breadcrumbHref = getDetailsPath?.({ id }) ?? checksPath;

  // Compute before early returns to respect React Hooks rules
  const resultRows = useMemo<ResultsTableRow[]>(() => {
    if (!checkDetails) return [];
    return checkDetails.results.map((result, index) => ({
      id: `${result.entityRef}-${index}`,
      entityRef: result.entityRef,
      checkResult: result.checkResult,
      entity: result.entity,
      status: result.checkResult.passed ? 'passed' : 'failed',
    }));
  }, [checkDetails]);

  const excludedRows = useMemo<ResultsTableRow[]>(() => {
    if (!checkDetails) return [];
    return checkDetails.excludedEntities.map(excluded => ({
      id: excluded.id,
      entityRef: excluded.entityRef,
      entity: excluded.entity,
      status: 'excluded',
    }));
  }, [checkDetails]);

  const tableRows = useMemo(
    () => [...resultRows, ...excludedRows],
    [resultRows, excludedRows],
  );

  const checkOwnerEntityRef = checkDetails?.check.ownerEntityRef
    ? normalizeOwnerEntityRef(checkDetails.check.ownerEntityRef)
    : undefined;

  const entityOwnerOptionsWithTeams = useMemo(
    () => buildEntityOwnerSections(teamOptions, groupOptions, selectedGroups),
    [teamOptions, groupOptions, selectedGroups],
  );
  const entityKindOptions = useMemo(
    () => getKindOptions(tableRows),
    [tableRows],
  );
  const lifecycleOptions = useMemo(
    () => getLifecycleOptions(tableRows),
    [tableRows],
  );

  const expandedSelectedGroups = useMemo(() => {
    if (selectedGroups.length === 0) return [];
    const scoped = new Set<string>();
    for (const ref of selectedGroups) {
      scoped.add(ref);
      for (const descendant of getDescendantOwnerRefs(ref, ownerByRef)) {
        scoped.add(descendant);
      }
    }
    return [...scoped];
  }, [selectedGroups, ownerByRef]);

  const filteredRows = useMemo(() => {
    let rows = filterRowsByGroups(tableRows, expandedSelectedGroups);
    rows = filterRowsByKinds(rows, filters.entityKinds);
    rows = filterRowsByLifecycles(rows, filters.lifecycles);
    return rows;
  }, [tableRows, expandedSelectedGroups, filters]);

  if (loading) {
    return (
      <BreadcrumbEntry entry={{ label: breadcrumbLabel, href: breadcrumbHref }}>
        <Header title={check?.name ?? id} description={check?.description} />
        <PulseDetailsSkeleton />
      </BreadcrumbEntry>
    );
  }

  if (error) {
    return (
      <BreadcrumbEntry entry={{ label: breadcrumbLabel, href: breadcrumbHref }}>
        <Header title={id} tags={[{ label: 'Back to Checks', href: '../' }]} />
        <Container>
          <Text>{`Error: ${error.message}`}</Text>
        </Container>
      </BreadcrumbEntry>
    );
  }

  if (!checkDetails) {
    return (
      <BreadcrumbEntry entry={{ label: breadcrumbLabel, href: breadcrumbHref }}>
        <EmptyState
          title="Check not found"
          description={`No check with id "${id}" was found.`}
        />
      </BreadcrumbEntry>
    );
  }

  const affectedCount = filteredRows.filter(
    row => row.status !== 'excluded',
  ).length;
  const passedCount = filteredRows.filter(
    row => row.status === 'passed',
  ).length;
  const failedCount = filteredRows.filter(
    row => row.status === 'failed',
  ).length;
  const passedPercentage =
    affectedCount > 0 ? Math.round((passedCount / affectedCount) * 100) : 0;

  const cadence = describeCheckCadence(checkDetails.check, integrations ?? []);
  const includedFilter = formatEntityFilter(checkDetails.check.filter);
  const excludedFilter = formatEntityFilter(checkDetails.check.exclude);
  const scopeFilter = excludedFilter
    ? `${includedFilter || 'All entities'}; excluding ${excludedFilter}`
    : includedFilter || 'All entities';

  return (
    <BreadcrumbEntry entry={{ label: breadcrumbLabel, href: breadcrumbHref }}>
      <Header
        title={checkDetails.check.name}
        description={checkDetails.check.description}
        customActions={
          <CheckActionsButton
            check={checkDetails.check}
            onDeleted={() => navigate('..')}
          />
        }
        metadata={[
          {
            label: 'Owner',
            value: checkOwnerEntityRef ? (
              <EntityRefLink entityRef={checkOwnerEntityRef} underline="none" />
            ) : (
              <Text color="secondary">
                {checkDetails.check.ownerEntityRef || '-'}
              </Text>
            ),
          },
          {
            label: cadence.label,
            value: cadence.value,
          },
          {
            label: 'Scope Filter',
            value: scopeFilter,
          },
          {
            label: 'Source',
            value:
              checkDetails.check.source === 'db'
                ? 'UI configured'
                : 'YAML configured',
          },
          // Timestamps only exist for UI-managed checks; hide when unavailable.
          ...(checkDetails.check.createdAt
            ? [
                {
                  label: 'Created',
                  value: formatTimestamp(checkDetails.check.createdAt),
                },
              ]
            : []),
          ...(checkDetails.check.updatedAt
            ? [
                {
                  label: 'Config Updated',
                  value: formatTimestamp(checkDetails.check.updatedAt),
                },
              ]
            : []),
        ]}
      />
      <Container>
        <Flex direction="column" gap="12">
          {savedNotice ? (
            <Alert
              status="success"
              icon
              title={describeCheckSavedNotice(
                checkDetails.check,
                integrations ?? [],
                savedNotice,
              )}
              customActions={
                <Text
                  variant="body-small"
                  onClick={() => setSavedNotice(null)}
                  style={{ cursor: 'pointer' }}
                >
                  Dismiss
                </Text>
              }
            />
          ) : null}
          <FilterBar
            entityOwnerOptions={entityOwnerOptionsWithTeams}
            selectedEntityOwners={selectedGroups}
            onEntityOwnersChange={setSelectedGroups}
            entityKindOptions={entityKindOptions}
            lifecycleOptions={lifecycleOptions}
            value={filters}
            onChange={setFilters}
          />
          <Flex direction="column" gap="6">
            <Grid.Root columns={{ initial: '1', lg: '2' }} gap="4">
              <Grid.Item>
                <Card style={{ height: '100%' }}>
                  <CardHeader>
                    <Text variant="body-large">Current Status</Text>
                  </CardHeader>
                  <CardBody>
                    <Flex
                      direction="column"
                      gap="2"
                      justify="center"
                      style={{ height: '100%' }}
                    >
                      <Flex direction="column" gap="1">
                        <Text variant="body-small" weight="bold">
                          {passedPercentage}% Passed
                        </Text>
                        <div
                          style={{
                            height: '8px',
                            borderRadius: 'var(--bui-radius-2)',
                            overflow: 'hidden',
                            backgroundColor: 'var(--bui-bg-neutral-2)',
                          }}
                        >
                          <div
                            style={{
                              height: '100%',
                              backgroundColor: 'var(--bui-fg-positive)',
                              width: `${passedPercentage}%`,
                              transition: 'width 0.3s ease',
                            }}
                          />
                        </div>
                      </Flex>
                      <Flex gap="1">
                        <Flex direction="column" gap="0" style={{ flex: 1 }}>
                          <Text variant="body-small" color="secondary">
                            Passed
                          </Text>
                          <Text
                            weight="bold"
                            variant="body-large"
                            color="success"
                          >
                            {passedCount}
                          </Text>
                        </Flex>
                        <Flex direction="column" gap="0" style={{ flex: 1 }}>
                          <Text variant="body-small" color="secondary">
                            Failed
                          </Text>
                          <Text
                            weight="bold"
                            variant="body-large"
                            color="danger"
                          >
                            {failedCount}
                          </Text>
                        </Flex>
                      </Flex>
                      <Text variant="body-small" color="secondary">
                        Total: {affectedCount}
                      </Text>
                    </Flex>
                  </CardBody>
                </Card>
              </Grid.Item>

              <Grid.Item>
                <CheckHistoryChartCard
                  checkId={id}
                  isFiltered={hasActiveEntitySelection(filters, selectedGroups)}
                />
              </Grid.Item>
            </Grid.Root>

            <ResultsTable
              data={filteredRows}
              error={error}
              columns={columns}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
            />
          </Flex>
        </Flex>
      </Container>
    </BreadcrumbEntry>
  );
};
